import {
  selectJBPSettingsActualsSync,
  selectJBPSettingsCurrentFYear,
  selectJBPSettingsSobActualRequiredQuarters,
  selectJBPSettingsStatus,
  useJbpSettings,
} from 'legacy/lut/features/partner360/jbpSettings'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { RequestStatus } from 'clickNgo/configs/service/common'
import {
  type FinancialMetricMode,
  resolveFinancialMetricMode,
} from 'partner360/entities/financialMetricMode'

import { buildSobActualsReviewPayload } from '../lib/buildSobActualsReviewPayload'
import { buildSobTableEntries } from '../lib/buildSobTableEntries'
import {
  buildInitialInputsByKey,
  buildNextInputsByKey,
  createSobInputState,
  flattenSobTableEntries,
  getEditableSobTableEntries,
  isIncompleteSobValue,
} from '../lib/sobInputState'
import {
  buildInitialNoPlanByKey,
  buildNextNoPlanByKey,
  createNoPlanEligibilityResolver,
  type NoPlanEligibility,
  type NoPlanStateByKey,
  selectEntryNoPlan,
  selectEntryNoPlanContractual,
} from '../lib/sobNoPlanState'
import {
  applyCategoryOverallRecalc,
  clearOverallRevertQuarters,
  getPendingRecalcQuarters,
  hasOverallRevertValues,
  hasPendingRecalcQuarters,
  resetPendingRecalcChanges,
  revertCategoryOverallRecalc,
  type SobRecalcStateByCategory,
} from '../lib/sobOverallRecalcState'
import {
  buildCurrentYearQuarterPolicies,
  buildEntryCurrentYearQuarterPolicies,
  buildPreviousYearQuarterPolicies,
} from '../lib/sobQuarterPolicies'
import {
  buildSobAvailableSettingsByKey,
  buildSobSettingsByCategoryKey,
  createSobNodeOrderResolver,
  filterSobAvailableFinDataRows,
} from '../lib/sobSettingsCollections'

import { DEFAULT_PY_QUARTER_POLICIES, SOB_QUARTER_KEYS } from './constants'
import type {
  SobActualsModalProps,
  SobInputKey,
  SobInputState,
  SobInputStateByKey,
  SobQuarterKey,
  SobQuarterPolicies,
  SobQuarterValues,
  SobTableEntry,
  SobTableEntryCategoryGroup,
} from './types'
import { useApplySobActualsReview } from './useApplySobActualsReview'
import { usePartnerPlanningInfo } from './usePartnerPlanningInfo'
import { useSobAvailabilitySettings } from './useSobAvailabilitySettings'

export type UseSobActualsModalContentParams = Omit<SobActualsModalProps, 'show' | 'onClose'> & {
  closeModal: () => void
}

export type SobActualsTableController = {
  showOverall: boolean
  currentYearQuarterPolicies: SobQuarterPolicies
  currentYearQuarterPoliciesByKey: Record<string, SobQuarterPolicies>
  previousYearQuarterPoliciesByKey: Record<string, SobQuarterPolicies>
  getEntryInputs: (entry: SobTableEntry) => SobInputState
  getEntryModifiedQuarters: (
    entry: SobTableEntry,
    inputKey: SobInputKey
  ) => Partial<Record<SobQuarterKey, boolean>>
  getEntryEffectiveNoPlan: (entry: SobTableEntry) => boolean
  getEntryNoPlan: (entry: SobTableEntry) => boolean
  getEntryNoPlanContractual: (entry: SobTableEntry) => boolean
  getNoPlanEligibility: (entry: SobTableEntry, isContractual: boolean) => NoPlanEligibility
  setEntryNoPlan: (entry: SobTableEntry) => (nextValue: boolean) => void
  setEntryNoPlanContractual: (entry: SobTableEntry) => (nextValue: boolean) => void
  setEntryInputValues: (
    entry: SobTableEntry,
    inputKey: SobInputKey
  ) => (nextValues: SobQuarterValues) => void
}

const groupTableEntriesByCategory = (
  tableEntries: SobTableEntry[]
): SobTableEntryCategoryGroup[] => {
  const groupsByCategory = tableEntries.reduce<Map<string, SobTableEntryCategoryGroup>>(
    (groups, entry) => {
      const group = groups.get(entry.category) ?? {
        category: entry.category,
        subCategoryTableEntries: [],
      }

      if (entry.isOverall) {
        group.overallTableEntry = entry
      } else {
        group.subCategoryTableEntries.push(entry)
      }

      groups.set(entry.category, group)

      return groups
    },
    new Map()
  )

  return Array.from(groupsByCategory.values())
}

export const useSobActualsModalContent = ({
  pid,
  offering,
  category,
  isBySubCategory,
  isNoPlanEditable = true,
  defaultMetricMode,
  availableMetricModes,
  currentFiscalYear,
  finData,
  onConfirmed,
  closeModal,
}: UseSobActualsModalContentParams) => {
  const [metricMode, setMetricMode] = useState<FinancialMetricMode>(() =>
    resolveFinancialMetricMode(defaultMetricMode, availableMetricModes)
  )
  const [showOverall, setShowOverall] = useState(false)
  const [showSobDetails, setShowSobDetails] = useState(false)
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false)
  const { submitSobActualsReview, isApplyingSobActualsReview, hasApplySobActualsReviewError } =
    useApplySobActualsReview()
  const { isLoaded: isJbpSettingsLoaded } = useJbpSettings()
  const jbpSettingsStatus = useSelector(selectJBPSettingsStatus)
  const jbpSettingsCurrentFYear = useSelector(selectJBPSettingsCurrentFYear)
  const sobActualRequiredQuarters = useSelector(selectJBPSettingsSobActualRequiredQuarters)
  const { fYearActuals, fQuarterActuals } = useSelector(selectJBPSettingsActualsSync)
  const { sobAvailabilitySettings, isSobAvailabilitySettingsLoaded } = useSobAvailabilitySettings({
    pid,
    currentFiscalYear,
    offering,
  })
  const { partnerFlags, sectionSettings, isPartnerPlanningInfoLoaded } = usePartnerPlanningInfo({
    pid,
    currentFiscalYear,
  })

  const sobAvailableSettingsByKey = useMemo(
    () =>
      buildSobAvailableSettingsByKey({
        settings: sobAvailabilitySettings,
        offering,
        category,
      }),
    [category, offering, sobAvailabilitySettings]
  )
  const sobSettingsByCategoryKey = useMemo(
    () =>
      buildSobSettingsByCategoryKey({
        settings: sobAvailabilitySettings,
        offering,
        category,
      }),
    [category, offering, sobAvailabilitySettings]
  )
  const filteredRows = useMemo(
    () =>
      filterSobAvailableFinDataRows({
        finData,
        pid,
        offering,
        category,
        sobAvailableSettingsByKey,
      }),
    [category, finData, offering, pid, sobAvailableSettingsByKey]
  )
  const resolvedMetricMode = useMemo(
    () => resolveFinancialMetricMode(metricMode, availableMetricModes),
    [availableMetricModes, metricMode]
  )
  // The `BySubCategory` action param is the only source of SOB details mandatoriness
  const isSobDetailsMandatory = isBySubCategory
  const shouldShowSobDetails = isSobDetailsMandatory || showSobDetails

  useEffect(() => {
    setMetricMode(resolveFinancialMetricMode(defaultMetricMode, availableMetricModes))
    setShowSobDetails(isSobDetailsMandatory)
  }, [availableMetricModes, defaultMetricMode, isSobDetailsMandatory, offering, pid])

  const getNodeOrder = useMemo(
    () => createSobNodeOrderResolver({ settingsByKey: sobAvailableSettingsByKey, offering }),
    [offering, sobAvailableSettingsByKey]
  )
  const sobAvailableTableEntries = useMemo(() => {
    if (!filteredRows.length) {
      return []
    }

    return buildSobTableEntries({
      rows: filteredRows,
      currentFiscalYear,
      metricMode: resolvedMetricMode,
      getNodeOrder,
    })
  }, [currentFiscalYear, filteredRows, getNodeOrder, resolvedMetricMode])
  const tableEntries = useMemo(
    () =>
      shouldShowSobDetails
        ? sobAvailableTableEntries
        : sobAvailableTableEntries.filter((entry) => entry.isOverall),
    [shouldShowSobDetails, sobAvailableTableEntries]
  )
  const allTableEntries = useMemo(() => flattenSobTableEntries(tableEntries), [tableEntries])
  const allSobAvailableTableEntries = useMemo(
    () => flattenSobTableEntries(sobAvailableTableEntries),
    [sobAvailableTableEntries]
  )
  const hasSubCategoryEntries = useMemo(
    () => tableEntries.some((entry) => !entry.isOverall),
    [tableEntries]
  )
  const tableEntryCategoryGroups = useMemo(
    () => groupTableEntriesByCategory(tableEntries),
    [tableEntries]
  )
  const editableTableEntries = useMemo(
    () => getEditableSobTableEntries(tableEntries),
    [tableEntries]
  )
  const previousYearQuarterPoliciesByKey = useMemo(
    () =>
      allTableEntries.reduce<Record<string, SobQuarterPolicies>>((acc, entry) => {
        acc[entry.key] = buildPreviousYearQuarterPolicies(entry.rows)

        return acc
      }, {}),
    [allTableEntries]
  )

  const [inputsByKey, setInputsByKey] = useState<SobInputStateByKey>(() =>
    buildInitialInputsByKey(tableEntries)
  )
  const [noPlanByKey, setNoPlanByKey] = useState<NoPlanStateByKey>(() =>
    buildInitialNoPlanByKey(tableEntries, selectEntryNoPlan)
  )
  const [noPlanContractualByKey, setNoPlanContractualByKey] = useState<NoPlanStateByKey>(() =>
    buildInitialNoPlanByKey(tableEntries, selectEntryNoPlanContractual)
  )
  const [recalcStateByCategory, setRecalcStateByCategory] = useState<SobRecalcStateByCategory>({})

  const inputsContextKey = useMemo(
    () => [pid, offering, category ?? '', currentFiscalYear].join('::'),
    [category, currentFiscalYear, offering, pid]
  )
  const inputsContextKeyRef = useRef(inputsContextKey)

  useEffect(() => {
    const isNewInputsContext = inputsContextKeyRef.current !== inputsContextKey

    inputsContextKeyRef.current = inputsContextKey

    if (isNewInputsContext) {
      setRecalcStateByCategory({})
    }

    setInputsByKey((currentInputsByKey) =>
      isNewInputsContext
        ? buildInitialInputsByKey(sobAvailableTableEntries)
        : buildNextInputsByKey(sobAvailableTableEntries, currentInputsByKey)
    )
  }, [inputsContextKey, sobAvailableTableEntries])

  useEffect(() => {
    setNoPlanByKey((currentNoPlanByKey) =>
      buildNextNoPlanByKey(sobAvailableTableEntries, currentNoPlanByKey, selectEntryNoPlan)
    )
    setNoPlanContractualByKey((currentNoPlanContractualByKey) =>
      buildNextNoPlanByKey(
        sobAvailableTableEntries,
        currentNoPlanContractualByKey,
        selectEntryNoPlanContractual
      )
    )
  }, [sobAvailableTableEntries])

  const getEntryInputs = useCallback(
    (entry: SobTableEntry): SobInputState =>
      inputsByKey[entry.key] ?? createSobInputState(entry.model),
    [inputsByKey]
  )
  const getEntryModifiedQuarters = useCallback(
    (entry: SobTableEntry, inputKey: SobInputKey): Partial<Record<SobQuarterKey, boolean>> => {
      const entryQuarterValues = getEntryInputs(entry)[inputKey]
      const initialQuarterValues = entry.model.sob[inputKey]

      return SOB_QUARTER_KEYS.reduce<Partial<Record<SobQuarterKey, boolean>>>((acc, quarterKey) => {
        acc[quarterKey] = entryQuarterValues[quarterKey] !== initialQuarterValues[quarterKey]

        return acc
      }, {})
    },
    [getEntryInputs]
  )
  const getEntryNoPlan = useCallback(
    (entry: SobTableEntry): boolean => noPlanByKey[entry.key] ?? entry.model.isNoPlan,
    [noPlanByKey]
  )
  const getEntryNoPlanContractual = useCallback(
    (entry: SobTableEntry): boolean =>
      noPlanContractualByKey[entry.key] ?? entry.model.isNoPlanContractual,
    [noPlanContractualByKey]
  )
  const getEntryEffectiveNoPlan = useCallback(
    (entry: SobTableEntry): boolean =>
      !entry.isOverall && (getEntryNoPlan(entry) || getEntryNoPlanContractual(entry)),
    [getEntryNoPlan, getEntryNoPlanContractual]
  )
  const setEntryNoPlan = useCallback(
    (entry: SobTableEntry) => (nextValue: boolean) => {
      setNoPlanByKey((currentNoPlanByKey) => ({
        ...currentNoPlanByKey,
        [entry.key]: nextValue,
      }))
    },
    []
  )
  const setEntryNoPlanContractual = useCallback(
    (entry: SobTableEntry) => (nextValue: boolean) => {
      setNoPlanContractualByKey((currentNoPlanContractualByKey) => ({
        ...currentNoPlanContractualByKey,
        [entry.key]: nextValue,
      }))
    },
    []
  )
  const setEntryInputValues = useCallback(
    (entry: SobTableEntry, inputKey: SobInputKey) => (nextValues: SobQuarterValues) => {
      const currentEntryInputs = inputsByKey[entry.key] ?? createSobInputState(entry.model)
      const changedQuarterKeys = SOB_QUARTER_KEYS.filter(
        (quarterKey) => currentEntryInputs[inputKey][quarterKey] !== nextValues[quarterKey]
      )

      setInputsByKey((currentInputs) => ({
        ...currentInputs,
        [entry.key]: {
          ...(currentInputs[entry.key] ?? createSobInputState(entry.model)),
          [inputKey]: nextValues,
        },
      }))

      // A manual overall edit stops being revertable; subcategory/L3 edits need no bookkeeping —
      // pending quarters are derived from the difference against the baseline values
      if (entry.isOverall && changedQuarterKeys.length) {
        setRecalcStateByCategory((currentState) =>
          clearOverallRevertQuarters(currentState, entry.category, inputKey, changedQuarterKeys)
        )
      }
    },
    [inputsByKey]
  )
  const getCategoryRecalcPrompt = useCallback(
    (targetCategory: string): 'recalculate' | 'cancel' | null => {
      const categoryState = recalcStateByCategory[targetCategory]
      const pendingQuarters = getPendingRecalcQuarters({
        entries: tableEntries,
        inputsByKey,
        category: targetCategory,
        state: categoryState,
      })

      if (hasPendingRecalcQuarters(pendingQuarters)) {
        return 'recalculate'
      }

      if (hasOverallRevertValues(categoryState)) {
        return 'cancel'
      }

      return null
    },
    [inputsByKey, recalcStateByCategory, tableEntries]
  )
  const handleRecalculateCategory = useCallback(
    (targetCategory: string) => {
      const result = applyCategoryOverallRecalc({
        entries: tableEntries,
        inputsByKey,
        stateByCategory: recalcStateByCategory,
        category: targetCategory,
      })

      setInputsByKey(result.inputsByKey)
      setRecalcStateByCategory(result.stateByCategory)
    },
    [inputsByKey, recalcStateByCategory, tableEntries]
  )
  const handleCancelCategoryRecalc = useCallback(
    (targetCategory: string) => {
      const result = revertCategoryOverallRecalc({
        entries: tableEntries,
        inputsByKey,
        stateByCategory: recalcStateByCategory,
        category: targetCategory,
      })

      setInputsByKey(result.inputsByKey)
      setRecalcStateByCategory(result.stateByCategory)
    },
    [inputsByKey, recalcStateByCategory, tableEntries]
  )
  const handleResetCategoryChanges = useCallback(
    (targetCategory: string) => {
      setInputsByKey(
        resetPendingRecalcChanges({
          entries: tableEntries,
          inputsByKey,
          category: targetCategory,
          state: recalcStateByCategory[targetCategory],
        })
      )
    },
    [inputsByKey, recalcStateByCategory, tableEntries]
  )

  const isWaitingForJbpSettings =
    !isJbpSettingsLoaded && jbpSettingsStatus !== RequestStatus.REJECTED
  const isWaitingForSobSettings =
    isWaitingForJbpSettings || !isSobAvailabilitySettingsLoaded || !isPartnerPlanningInfoLoaded
  const isApplicableCurrentFiscalYear =
    isJbpSettingsLoaded && currentFiscalYear === jbpSettingsCurrentFYear
  const hasUsableSobActualSettings =
    isApplicableCurrentFiscalYear && sobActualRequiredQuarters.length === SOB_QUARTER_KEYS.length
  const shouldShowSettingsUnavailableWarning =
    jbpSettingsStatus === RequestStatus.REJECTED ||
    (isApplicableCurrentFiscalYear && !hasUsableSobActualSettings)

  const currentYearQuarterPolicies = useMemo<SobQuarterPolicies>(
    () =>
      buildCurrentYearQuarterPolicies({
        hasUsableSobActualSettings,
        sobActualRequiredQuarters,
        currentFiscalYear,
        actualsPeriod:
          fYearActuals && fQuarterActuals
            ? { fYear: fYearActuals, fQuarter: fQuarterActuals }
            : null,
      }),
    [
      currentFiscalYear,
      fQuarterActuals,
      fYearActuals,
      hasUsableSobActualSettings,
      sobActualRequiredQuarters,
    ]
  )
  const currentYearQuarterPoliciesByKey = useMemo(
    () =>
      allTableEntries.reduce<Record<string, SobQuarterPolicies>>((acc, entry) => {
        acc[entry.key] = entry.isOverall
          ? currentYearQuarterPolicies
          : buildEntryCurrentYearQuarterPolicies({
              rows: entry.rows,
              currentYearQuarterPolicies,
            })

        return acc
      }, {}),
    [allTableEntries, currentYearQuarterPolicies]
  )
  const getNoPlanEligibility = useMemo(
    () =>
      createNoPlanEligibilityResolver({
        offering,
        isNoPlanEditable,
        partnerFlags,
        sectionSettings,
        sobAvailableSettingsByKey,
        sobSettingsByCategoryKey,
        allSobAvailableTableEntries,
        getEntryNoPlan,
        getEntryNoPlanContractual,
      }),
    [
      allSobAvailableTableEntries,
      getEntryNoPlan,
      getEntryNoPlanContractual,
      isNoPlanEditable,
      offering,
      partnerFlags,
      sectionSettings,
      sobAvailableSettingsByKey,
      sobSettingsByCategoryKey,
    ]
  )
  const hasIncompleteRequiredFields = useMemo(() => {
    const hasIncompletePreviousYearActuals = editableTableEntries.some((entry) => {
      const previousYearQuarterPolicies =
        previousYearQuarterPoliciesByKey[entry.key] ?? DEFAULT_PY_QUARTER_POLICIES
      const entryInputs = getEntryInputs(entry)

      return SOB_QUARTER_KEYS.some(
        (quarterKey) =>
          previousYearQuarterPolicies[quarterKey] === 'required' &&
          isIncompleteSobValue(entryInputs.prevActuals[quarterKey])
      )
    })

    if (hasIncompletePreviousYearActuals) {
      return true
    }

    return editableTableEntries.some((entry) => {
      if (getEntryEffectiveNoPlan(entry)) {
        return false
      }

      const entryCurrentYearQuarterPolicies =
        currentYearQuarterPoliciesByKey[entry.key] ?? currentYearQuarterPolicies
      const entryInputs = getEntryInputs(entry)

      return SOB_QUARTER_KEYS.some(
        (quarterKey) =>
          entryCurrentYearQuarterPolicies[quarterKey] === 'required' &&
          isIncompleteSobValue(entryInputs.currentActuals[quarterKey])
      )
    })
  }, [
    currentYearQuarterPolicies,
    currentYearQuarterPoliciesByKey,
    editableTableEntries,
    getEntryEffectiveNoPlan,
    getEntryInputs,
    previousYearQuarterPoliciesByKey,
  ])

  useEffect(() => {
    if (!hasIncompleteRequiredFields && showIncompleteWarning) {
      setShowIncompleteWarning(false)
    }
  }, [hasIncompleteRequiredFields, showIncompleteWarning])

  const applyChanges = useCallback(async () => {
    const sob = buildSobActualsReviewPayload({
      entries: allTableEntries,
      pid,
      currentFiscalYear,
      offering,
      category,
      getEntryInputs,
      getEntryNoPlan,
      getEntryNoPlanContractual,
    })
    const isApplied = await submitSobActualsReview({
      partnerId: pid,
      fYear: currentFiscalYear,
      sob,
    })

    if (!isApplied) {
      return
    }

    closeModal()
    await onConfirmed?.()
  }, [
    allTableEntries,
    category,
    closeModal,
    currentFiscalYear,
    getEntryInputs,
    getEntryNoPlan,
    getEntryNoPlanContractual,
    offering,
    onConfirmed,
    pid,
    submitSobActualsReview,
  ])

  const handleSaveClick = useCallback(() => {
    if (isWaitingForSobSettings) {
      return
    }

    if (hasIncompleteRequiredFields) {
      setShowIncompleteWarning(true)
      return
    }

    void applyChanges()
  }, [applyChanges, hasIncompleteRequiredFields, isWaitingForSobSettings])
  const handleSaveAsIs = useCallback(() => {
    setShowIncompleteWarning(false)
    void applyChanges()
  }, [applyChanges])
  const handleReturnToFilling = useCallback(() => {
    setShowIncompleteWarning(false)
  }, [])

  return {
    resolvedMetricMode,
    setMetricMode,
    showOverall,
    setShowOverall,
    shouldShowSobDetails,
    isSobDetailsMandatory,
    setShowSobDetails,
    tableEntries,
    tableEntryCategoryGroups,
    hasSubCategoryEntries,
    isWaitingForSobSettings,
    shouldShowSettingsUnavailableWarning,
    isApplyingSobActualsReview,
    hasApplySobActualsReviewError,
    showIncompleteWarning,
    handleSaveClick,
    handleSaveAsIs,
    handleReturnToFilling,
    getCategoryRecalcPrompt,
    handleRecalculateCategory,
    handleCancelCategoryRecalc,
    handleResetCategoryChanges,
    tableController: {
      showOverall,
      currentYearQuarterPolicies,
      currentYearQuarterPoliciesByKey,
      previousYearQuarterPoliciesByKey,
      getEntryInputs,
      getEntryModifiedQuarters,
      getEntryEffectiveNoPlan,
      getEntryNoPlan,
      getEntryNoPlanContractual,
      getNoPlanEligibility,
      setEntryNoPlan,
      setEntryNoPlanContractual,
      setEntryInputValues,
    } satisfies SobActualsTableController,
  }
}

export type SobActualsModalContentViewModel = ReturnType<typeof useSobActualsModalContent>
